import { clsx } from 'clsx'
import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

export function SimpleTransition({
  show,
  children,
  enter,
  enterFrom,
  enterTo,
  entered,
  leave,
  leaveFrom,
  leaveTo,
}: {
  show: boolean
  children: ReactNode
  enter?: string
  enterFrom?: string
  enterTo?: string
  entered?: string
  leave?: string
  leaveFrom?: string
  leaveTo?: string
}) {
  const [componentState, setComponentState] = useState<
    | 'visible'
    | 'hidden'
    | 'transition-in-start'
    | 'transition-in'
    | 'transition-out-start'
    | 'transition-out'
  >(() => 'hidden')
  const childRef = useRef<HTMLElement | undefined>(undefined)

  useEffect(
    () => {
      const buttonEl = childRef.current
      if (!buttonEl) {
        return
      }

      if (show && componentState !== 'visible') {
        setComponentState('transition-in-start')

        const setVisible = () => setComponentState('visible')
        const rafId = requestAnimationFrame(() => {
          buttonEl.addEventListener('transitionend', setVisible, {
            once: true,
          })
          setComponentState('transition-in')
        })
        return () => {
          cancelAnimationFrame(rafId)
          buttonEl.removeEventListener('transitionend', setVisible)
          if (
            componentState === 'transition-in-start' ||
            componentState === 'transition-in'
          ) {
            setComponentState('visible')
          }
        }
      } else if (!show && componentState !== 'hidden') {
        setComponentState('transition-out-start')

        const setHidden = () => setComponentState('hidden')
        const rafId = requestAnimationFrame(() => {
          buttonEl.addEventListener('transitionend', setHidden, {
            once: true,
          })
          setComponentState('transition-out')
        })
        buttonEl.addEventListener('transitionend', setHidden, {
          once: true,
        })
        return () => {
          cancelAnimationFrame(rafId)
          buttonEl.removeEventListener('transitionend', setHidden)
          if (
            componentState === 'transition-out-start' ||
            componentState === 'transition-out'
          ) {
            setComponentState('hidden')
          }
        }
      }
    }, // we don't want to listen to componentState changes, this is only needed as an additional guard
    // to prevent unnecessary transitions and intermediate states after cleanups
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show],
  )

  const onlyChild = Children.only(children)
  if (!isValidElement(onlyChild)) {
    throw new Error(
      'Transition component expects a single React element as its child!',
    )
  }

  // React 19 narrowed isValidElement's props to `unknown`; cast to keep the
  // previous `any` semantics so cloneElement accepts arbitrary props/ref.
  const elementChild = onlyChild as ReactElement<any>
  const { children: elChildren, ...props } = elementChild.props
  return cloneElement(
    elementChild,
    {
      ...props,
      ref: (ref: any) => (childRef.current = ref),
      className: clsx(
        props.className,
        componentState === 'transition-in' && enter,
        componentState === 'transition-out' && leave,
        componentState === 'visible' && entered,
        componentState === 'transition-in-start' && enterFrom,
        componentState === 'transition-in' && enterTo,
        componentState === 'transition-out-start' && leaveFrom,
        componentState === 'transition-out' && leaveTo,
        componentState === 'hidden' && 'hidden',
      ),
    },
    elChildren,
  )
}
